import NumberFormat from 'react-number-format';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { Bonus } from '@/types';
import { useUpdateBonusMutation } from '@/services';
import { messages } from '@/utils/helpers';

export interface UpdateBonusProps extends DialogProps {
  bonus: Bonus;
  onClose(): void;
}

const UpdateBonus: React.FC<UpdateBonusProps> = ({ bonus, onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateBonus, updateBonusResponse] = useUpdateBonusMutation();

  const { values, errors, touched, handleSubmit, handleChange } = useFormik<Bonus>({
    initialValues: bonus ?? {
      name: '',
      is_name_editable: false,
      percent: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(messages.form.REQUIRED),
      percent: yup.string().required(messages.form.REQUIRED),
    }),
    async onSubmit(bonus, { resetForm }) {
      try {
        await updateBonus(bonus).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Редактировать бонусную программу</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={updateBonusResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <NumberFormat
                customInput={TextField}
                name="percent"
                type="tel"
                label="Процент"
                placeholder="Введите значение"
                value={values.percent}
                onChange={handleChange}
                disabled={updateBonusResponse.isLoading}
                error={touched.percent && Boolean(errors.percent)}
                helperText={touched.percent ? errors.percent : ''}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={updateBonusResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={updateBonusResponse.isLoading}>
            Редактировать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateBonus;
